import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface BdOverview {
  bd: {
    _id: string;
    fullName: string;
    email: string;
    commissionPercent: number;
  };
  totals: {
    revenue: number;
    bdEarnings: number;
    companyNet: number;
    laborCost: number;
    upworkFee: number;
    accountCommission: number;
    projects: number;
    activeProjects: number;
    completedProjects: number;
    bids: number;
    jobsWon: number;
    winRate: number;
    clients: number;
    proposals: number;
    questionnaires: number;
  };
}

export interface BdProfile {
  accountId: string;
  name: string;
  rankingBadge: string | null;
  jobSuccessRate: number | null;
  totalEarnings: number;
  totalJobs: number;
  totalHours: number;
  conversionRate: number;
  connectsUsed: number;
  totalConnects: number;
  connectsUsedPct: number;
  totalBids: number;
  jobsWon: number;
  dataSource: "screenshot" | "scraped" | "manual";
  lastSnapshotAt: string | null;
  activeProjects: number;
}

export interface CommissionBreakdown {
  revenue: number;
  upworkFeePct: number;
  upworkFeeAmount: number;
  accountCommissionPct: number;
  accountCommissionAmount: number;
  bdCommissionPct: number;
  bdCommissionAmount: number;
  laborCost: number;
  companyNet: number;
}

export interface MilestoneItem {
  order: number;
  title: string;
  amount: number;
  status: "Pending" | "In Escrow" | "Released" | string;
  dueDate: string | null;
  releasedAt: string | null;
  notes: string;
}

export interface BdProfileProject {
  _id: string;
  name: string;
  projectType: string;
  projectStatus: string;
  priceModel: "fixed" | "hourly";
  assignedTo: string;
  completionPct: number;
  milestonesReleased: number;
  milestonesTotal: number;
  milestoneItems: MilestoneItem[];
  total: number;
  escrow: number;
  released: number;
  origin: "BID" | "ORGANIC";
  breakdown: CommissionBreakdown;
}

export interface BdPerformanceCard {
  accountId: string;
  accountName: string;
  metrics: {
    avgConnectsPerBid: number;
    avgProjectAmount: number;
    topConvertingCategory: string | null;
    topCategoryConversionPct: number;
    avgProposalsPerMonth: number;
    bidEfficiencyScore: number;
    totalBids: number;
    totalConnects: number;
    wonBids: number;
    winRate: number;
    completedProjects: number;
    totalRevenue: number;
  };
}

export interface BdCostConfig {
  _id: string;
  projectType: string;
  clientTier: "Basic" | "Standard" | "Premium";
  upworkAccount: { _id: string; name: string } | null;
  fixedCost: { amount: number; type: string; hours: number };
  rangeCost: { min: number; max: number };
  graphicDesignerCost: { amount: number; type: string; hours: number };
  coordinatorCost: { amount: number; type: string; hours: number };
  commission: number;
  upworkFee: number;
  otherFees: { label: string; amount: number; type: string };
  notes: string;
  extraRoles: { label: string; amount: number; type: string; hours: number }[];
}

export interface ConnectsMonth {
  year: number;
  month: number;
  label: string;
  connectsUsed: number;
  connectsCost: number;
  projectsWon: number;
  revenue: number;
  released: number;
  opening: number;
  due: number;
  recovered: number;
  outstanding: number;
  recoveryRate: number;
}

export interface ConnectsRecovery {
  costPerConnect: number;
  currentOutstanding: number;
  totalConnectsUsed: number;
  totalConnectsCost: number;
  totalRecovered: number;
  totalRevenue: number;
  current: ConnectsMonth | null;
  months: ConnectsMonth[];
}

interface BdState {
  loading: boolean;
  overview: BdOverview | null;
  profiles: BdProfile[];
  profileProjects: Record<string, BdProfileProject[]>;
  performance: { period: string; accounts: BdPerformanceCard[] } | null;
  costConfigs: BdCostConfig[];
  connectsRecovery: ConnectsRecovery | null;

  fetchOverview: () => Promise<void>;
  fetchProfiles: () => Promise<void>;
  fetchProfileProjects: (profileId: string) => Promise<void>;
  fetchPerformance: (period?: string) => Promise<void>;
  fetchCostConfigs: () => Promise<void>;
  fetchConnectsRecovery: () => Promise<void>;
}

export const useBdStore = create<BdState>((set, get) => ({
  loading: false,
  overview: null,
  profiles: [],
  profileProjects: {},
  performance: null,
  costConfigs: [],
  connectsRecovery: null,

  fetchOverview: async () => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/bd/overview");
      set({ overview: data });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load overview");
    } finally {
      set({ loading: false });
    }
  },

  fetchProfiles: async () => {
    try {
      const { data } = await axiosInstance.get("/bd/profiles");
      set({ profiles: data.profiles });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load profiles");
    }
  },

  fetchProfileProjects: async (profileId) => {
    try {
      const { data } = await axiosInstance.get(
        `/bd/profiles/${profileId}/projects`
      );
      set({
        profileProjects: {
          ...get().profileProjects,
          [profileId]: data.projects,
        },
      });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load projects");
    }
  },

  fetchPerformance: async (period = "all") => {
    try {
      const { data } = await axiosInstance.get("/bd/performance", {
        params: { period },
      });
      set({ performance: data });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load performance");
    }
  },

  fetchCostConfigs: async () => {
    try {
      const { data } = await axiosInstance.get("/bd/cost-configs");
      set({ costConfigs: data.items });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load cost configs");
    }
  },

  fetchConnectsRecovery: async () => {
    try {
      const { data } = await axiosInstance.get("/bd/connects-recovery");
      set({ connectsRecovery: data });
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load connects recovery");
    }
  },
}));
